import { useCallback, useSyncExternalStore } from 'react';

import { errorMessage } from '../services/apiError';

interface ClaimBatchState { busy: boolean; error: string | null }
const idle: ClaimBatchState = { busy: false, error: null };
const batches = new Map<string, ClaimBatchState>();
const subscribers = new Map<string, Set<() => void>>();

const releaseUnusedBatch = (scope: string) => {
  if (!subscribers.get(scope)?.size && !batches.get(scope)?.busy) {
    subscribers.delete(scope);
    batches.delete(scope);
  }
};

const subscribe = (scope: string, listener: () => void) => {
  const listeners = subscribers.get(scope) ?? new Set<() => void>();

  subscribers.set(scope, listeners);
  listeners.add(listener);

  return () => { listeners.delete(listener); releaseUnusedBatch(scope); };
};

const updateBatch = (scope: string, state: ClaimBatchState) => {
  batches.set(scope, state);
  subscribers.get(scope)?.forEach((listener) => listener());
  releaseUnusedBatch(scope);
};

/** The claim operation survives source changes and responsive action-bar remounts. */
export const useRewardClaimBatch = (scope: string) => {
  const listen = useCallback((listener: () => void) => subscribe(scope, listener), [scope]);
  const snapshot = useCallback(() => batches.get(scope) ?? idle, [scope]);
  const state = useSyncExternalStore(listen, snapshot, () => idle);
  const run = async (claim: () => Promise<void>) => {
    if (batches.get(scope)?.busy) return;
    updateBatch(scope, { busy: true, error: null });
    try {
      await claim();
      updateBatch(scope, idle);
    } catch (error) {
      updateBatch(scope, { busy: false, error: errorMessage(error, 'Could not claim the reward. Try again.') });
    }
  };

  return { ...state, run };
};
